/**
 * Ablage einer aus dem Postfach gezogenen Mail bzw. ihrer Anhänge (User
 * 01.11.2026) — gemeinsam für die Vorgangskarten (`DateiDropZoneV4`) und die
 * Dokumente eines Kontakts (`DocumentsSection`).
 *
 * Keine neuen Server-Wege, sondern die vorhandenen:
 * - Mail → `POST /mail/links` (wie „Mail an Vorgang/To-do hängen“ aus der
 *   Mailliste). Der Server legt die Mail dabei dauerhaft als `.eml` ab — beim
 *   Kunden des Vorgangs bzw. beim Kontakt — und übernimmt ihre Anhänge.
 * - Anhänge → `POST /mail/attachments/import` (wie „Beim Kontakt speichern“),
 *   ohne Kunden mit dem Vorgang als Ablageziel, wie ein Datei-Upload auf der
 *   Karte.
 *
 * Gelesen wird der Zug SYNCHRON im `drop` ({@link leseAblageZug}): Nach dem
 * ersten `await` gibt der Browser den Inhalt des `DataTransfer` nicht mehr her.
 */
import { documentService } from "../../services/documentService";
import { graphMailService } from "../../services/graphMailService";
import { mailLinkService, type MailAblageOrt, type MailDropAblage } from "../../services/mailLinkService";
import { ZIEHTYP_MAIL_ANHANG, ZIEHTYP_POSTFACH_MAIL } from "../vorgang/detail/todoDrop";
import { istNeuesOutlookZug } from "./neuesOutlook";
import { leseAnhaengeZug, lesePostfachMailZug, type AnhaengeZug, type PostfachMailZug } from "./postfachZug";

export type AblageZiel =
    | { vorgangId: number; todoId?: number | null }
    | {
          contactId: number;
          /**
           * Die offene Ansicht der Dokumente (Kategorie, Ordner der Alten
           * Kundenakte): Die Mail und ihre Anhänge erscheinen GENAU DORT
           * (User 01.11.2026).
           */
          ort?: MailAblageOrt | null;
      };

export type AblageZug =
    | {
          art: "postfachMail";
          mail: PostfachMailZug;
          /** „Nur die Mail“ gewählt (User 03.11.2026) — Anhänge bleiben in der Mail. */
          ohneAnhaenge?: boolean;
      }
    | { art: "anhang"; anhaenge: AnhaengeZug };

export type AblageErgebnis = {
    /** Neu abgelegt bzw. verknüpft. */
    abgelegt: number;
    /** Lag schon dort. */
    vorhanden: number;
    /** Namen dessen, was nicht ging. */
    fehlgeschlagen: string[];
    /** Satz für den Nutzer. */
    meldung: string;
    /** Ids der entstandenen bzw. schon vorhandenen Dokumente (Mail: `.eml` + Anhänge). */
    dokumentIds?: number[];
    /** Der Server war noch nicht fertig — Nachladen lohnt sich später noch einmal. */
    laeuft?: boolean;
};

/**
 * Welche Postfach-Nutzlast trägt der Zug? Anders als {@link dropArt} zählt
 * hier eine zusätzlich mitreisende Vorgangs-Mail (`application/x-kmvs-mail`)
 * nicht: Eine Mail aus der Ereignisliste des Vorgangs trägt beide Typen, damit
 * sie in die Dokumente eines Kontakts gezogen werden kann — die Vorgangskarten
 * prüfen weiter mit `dropArt` und verknüpfen sie nicht ein zweites Mal.
 */
export function postfachArt(typen: readonly string[] | undefined): "postfachMail" | "anhang" | null {
    const liste = typen ?? [];
    if (liste.includes(ZIEHTYP_POSTFACH_MAIL)) return "postfachMail";
    if (liste.includes(ZIEHTYP_MAIL_ANHANG)) return "anhang";
    return null;
}

/**
 * Was eine Ablagezone für die Dokumente bzw. die Alte Kundenakte annimmt:
 * Postfach-Mail, Anhänge — und seit 03.11.2026 Mails aus dem neuen Outlook
 * (`maillistrow`, siehe `neuesOutlook.ts`). Ohne diesen Typ zeigt Edge beim
 * Ziehen aus dem neuen Outlook das Verbotszeichen.
 */
export type FremdAblageArt = "postfachMail" | "anhang" | "neuesOutlook";

export function fremdAblageArt(typen: readonly string[] | undefined): FremdAblageArt | null {
    return postfachArt(typen) ?? (istNeuesOutlookZug(typen) ? "neuesOutlook" : null);
}

/** Wort für den Hinweis der Ablagezone („Mail … ablegen“). */
export function fremdAblageWas(art: FremdAblageArt): string {
    return art === "anhang" ? "Anhang" : art === "neuesOutlook" ? "Mail aus Outlook" : "Mail";
}

/** Trägt der Zug (beim Überfahren schon erkennbar) eine Postfach-Mail oder Anhänge? */
export function ziehtAusPostfach(typen: readonly string[] | undefined): boolean {
    return postfachArt(typen) != null;
}

/** Den Zug beim `drop` lesen — synchron, solange der Browser ihn noch hergibt. */
export function leseAblageZug(dt: DataTransfer | null | undefined): AblageZug | null {
    if (!dt) return null;
    const art = postfachArt(Array.from(dt.types ?? []));
    if (art === "postfachMail") {
        const mail = lesePostfachMailZug(dt.getData(ZIEHTYP_POSTFACH_MAIL));
        return mail ? { art, mail } : null;
    }
    if (art === "anhang") {
        const anhaenge = leseAnhaengeZug(dt.getData(ZIEHTYP_MAIL_ANHANG));
        return anhaenge ? { art, anhaenge } : null;
    }
    return null;
}

const zielText = (ziel: AblageZiel): string =>
    "contactId" in ziel ? "beim Kontakt" : ziel.todoId != null ? "am To-do" : "am Vorgang";

const fehlerText = (err: unknown): string => {
    const roh = err instanceof Error ? err.message : String(err ?? "");
    if (/\(403\)|403/.test(roh)) return "keine Berechtigung";
    if (/\(404\)|404/.test(roh)) return "nicht gefunden";
    return "unbekannter Fehler";
};

/**
 * Mail verknüpfen und SOFORT samt Anhängen ablegen (User 01.11.2026: „Wenn
 * ich eine Mail rüberziehe, dann die Anhänge automatisch extrahieren und mit
 * reinpacken“). Der Server sichert die `.eml`, packt die echten Anhänge (ohne
 * Inline-Bilder, S/MIME ausgepackt) ins Ziel und meldet die Dokument-Ids —
 * ein erneuter Drop legt nichts doppelt ab.
 */
async function legeMailAb(mail: PostfachMailZug, ziel: AblageZiel, ohneAnhaenge = false): Promise<AblageErgebnis> {
    const name = mail.subject || "Ohne Betreff";
    try {
        const zielFelder =
            "contactId" in ziel
                ? { contactId: ziel.contactId }
                : ziel.todoId != null
                  ? { todoId: ziel.todoId }
                  : { vorgangId: ziel.vorgangId };
        const ort = "contactId" in ziel ? ziel.ort ?? { kategorie: null, ordner: null } : null;
        // „Nur die Mail“ nur mitschicken, wenn gewählt — sonst bleibt der Aufruf wie bisher.
        const link = ohneAnhaenge
            ? await mailLinkService.createMitAblage({ ...mail, ...zielFelder }, undefined, ort, true)
            : await mailLinkService.createMitAblage({ ...mail, ...zielFelder }, undefined, ort);
        const ablage = link.ablage;
        const dokumentIds = ablage
            ? [...(ablage.emlDocumentId != null ? [ablage.emlDocumentId] : []), ...ablage.anhangDocumentIds]
            : [];
        return {
            abgelegt: 1,
            vorhanden: 0,
            fehlgeschlagen: [],
            dokumentIds,
            laeuft: !!ablage?.laeuft,
            meldung: mailMeldung(name, ziel, ablage),
        };
    } catch (err) {
        return {
            abgelegt: 0,
            vorhanden: 0,
            fehlgeschlagen: [name],
            dokumentIds: [],
            meldung: `Die Mail „${name}“ konnte nicht ${zielText(ziel)} abgelegt werden (${fehlerText(err)}).`,
        };
    }
}

/** Satz nach dem Drop einer Mail — sagt, was mit den Anhängen ist. */
export function mailMeldung(name: string, ziel: AblageZiel, ablage: MailDropAblage | undefined): string {
    const wo = "contactId" in ziel ? "beim Kontakt unter „Dokumente“" : zielText(ziel);
    if (!ablage || ablage.status === "aus") return `Die Mail „${name}“ hängt jetzt ${zielText(ziel)}.`;
    if (ablage.laeuft) return `Die Mail „${name}“ hängt ${zielText(ziel)} – die Ablage samt Anhängen läuft noch und erscheint gleich.`;
    if (ablage.status === "fehlgeschlagen") {
        return `Die Mail „${name}“ hängt ${zielText(ziel)}, konnte aber noch nicht als Datei abgelegt werden${ablage.fehler ? ` (${ablage.fehler})` : ""}.`;
    }
    const n = ablage.anhangDocumentIds.length;
    const anhaenge = n === 0 ? "" : n === 1 ? " – mit 1 Anhang" : ` – mit ${n} Anhängen`;
    const fehler = ablage.anhaengeFehlgeschlagen
        ? ` ${ablage.anhaengeFehlgeschlagen === 1 ? "1 Anhang ließ" : `${ablage.anhaengeFehlgeschlagen} Anhänge ließen`} sich nicht übernehmen.`
        : "";
    return `Die Mail „${name}“ liegt jetzt ${wo}${anhaenge}.${fehler}`;
}

/** Anhänge über Graph holen und als Dokument ablegen. */
async function legeAnhaengeAb(zug: AnhaengeZug, ziel: AblageZiel): Promise<AblageErgebnis> {
    const namen = new Map(zug.anhaenge.map((a) => [`${a.messageId}\u0000${a.attachmentId}`, a.name]));
    const nameVon = (messageId: string, attachmentId: string) =>
        namen.get(`${messageId}\u0000${attachmentId}`) ?? "Anhang";
    const items = zug.anhaenge.map((a) => ({ messageId: a.messageId, attachmentId: a.attachmentId }));
    let res;
    try {
        res = await graphMailService.importAttachments({
            mailbox: zug.mailbox,
            ...("contactId" in ziel
                ? { contactId: ziel.contactId, kategorie: ziel.ort?.kategorie ?? null, ordner: ziel.ort?.ordner ?? null }
                : { vorgangId: ziel.vorgangId, todoId: ziel.todoId ?? null }),
            attachments: items,
        });
    } catch (err) {
        return {
            abgelegt: 0,
            vorhanden: 0,
            fehlgeschlagen: zug.anhaenge.map((a) => a.name),
            meldung: `${zug.anhaenge.length === 1 ? `„${zug.anhaenge[0].name}“ konnte` : "Die Anhänge konnten"} nicht ${zielText(ziel)} abgelegt werden (${fehlerText(err)}).`,
        };
    }

    let abgelegt = 0;
    let vorhanden = 0;
    const fehlgeschlagen: string[] = [];
    for (const r of res.results) {
        if (r.status === "error") {
            fehlgeschlagen.push(nameVon(r.messageId, r.attachmentId));
            continue;
        }
        if (r.status === "duplicate") {
            vorhanden += 1;
            continue;
        }
        // Am To-do: Der Server legt neu am Vorgang ab — wie ein Upload auf die
        // Karte gehört es aber ans To-do. Verknüpftes (`linked`) hat er dort
        // schon selbst eingehängt.
        if (r.status === "created" && !("contactId" in ziel) && ziel.todoId != null && r.documentId != null) {
            try {
                await documentService.update(r.documentId, { todoId: ziel.todoId });
            } catch {
                // Liegt dann am Vorgang statt am To-do — nicht verloren.
            }
        }
        abgelegt += 1;
    }
    const dokumentIds = res.results
        .filter((r) => r.status !== "error" && r.documentId != null)
        .map((r) => r.documentId as number);

    const teile: string[] = [];
    if (abgelegt > 0) teile.push(abgelegt === 1 ? `1 Anhang ${zielText(ziel)} abgelegt` : `${abgelegt} Anhänge ${zielText(ziel)} abgelegt`);
    if (vorhanden > 0) teile.push(vorhanden === 1 ? "1 lag schon dort" : `${vorhanden} lagen schon dort`);
    if (fehlgeschlagen.length > 0) teile.push(`fehlgeschlagen: ${fehlgeschlagen.join(", ")}`);
    return { abgelegt, vorhanden, fehlgeschlagen, dokumentIds, meldung: `${teile.join(", ")}.` };
}

export function legeZugAb(zug: AblageZug, ziel: AblageZiel): Promise<AblageErgebnis> {
    return zug.art === "postfachMail" ? legeMailAb(zug.mail, ziel, zug.ohneAnhaenge === true) : legeAnhaengeAb(zug.anhaenge, ziel);
}
